/** One license held by a user, as returned by the user-licenses backend route. */
export interface UserLicenseEntry {
  id: string;
  licenseTypeKey: string;
  licenseTypeName: string;
  startDate: string;
}

/**
 * Body of the `GET /licenses` response.
 *
 * The envelope used to exist only as an object literal in the router and a
 * string check in the frontend hook, each proven by its own hand-written
 * fixture. Renaming the key on one side left the other side's tests green and
 * broke every request at runtime. Typing both ends against this makes that
 * rename a compile error instead.
 */
export interface UserLicensesResponse {
  items: UserLicenseEntry[];
  /**
   * `true` when the upstream held more licences than one page and the list
   * above is therefore incomplete. A partial answer must be expressible in the
   * response, otherwise the consumer cannot tell it apart from a complete one.
   */
  truncated: boolean;
}

/**
 * Sub-path of the licences route, below the plugin's mount point.
 *
 * The mount point itself is `USER_LICENSES_PLUGIN_ID`. These two together are
 * the whole address, and both halves are read by the router that serves it and
 * the hook that calls it, so a rename moves both ends at once instead of leaving
 * the frontend pointing at a path that no longer exists.
 */
export const USER_LICENSES_ROUTE_PATH = 'licenses';

/** Query parameter carrying the principal name to look up. */
export const USER_LICENSES_UPN_QUERY_PARAM = 'userPrincipalName';

/**
 * Every key of {@link UserLicenseEntry}, as runtime data.
 *
 * The frontend validates the wire payload with a hand-written guard (the shared
 * package deliberately carries no `zod`, which has no place in a browser
 * bundle). A hand-written guard is a *claim* the compiler accepts rather than a
 * shape it checks, so it silently stopped matching whenever a field was renamed
 * or added. Driving the guard off this array closes that: `satisfies` breaks on
 * a rename, and the exhaustiveness check below breaks on an addition.
 */
export const USER_LICENSE_ENTRY_KEYS = [
  'id',
  'licenseTypeKey',
  'licenseTypeName',
  'startDate',
] as const satisfies readonly (keyof UserLicenseEntry)[];

/**
 * Compile-time proof that {@link USER_LICENSE_ENTRY_KEYS} names every key. Adding
 * a field to {@link UserLicenseEntry} without listing it here makes this type
 * resolve to an error tuple naming the missing key.
 */
export type UserLicenseEntryKeysAreExhaustive = Exclude<
  keyof UserLicenseEntry,
  (typeof USER_LICENSE_ENTRY_KEYS)[number]
> extends never
  ? true
  : [
      'USER_LICENSE_ENTRY_KEYS is missing keys',
      Exclude<keyof UserLicenseEntry, (typeof USER_LICENSE_ENTRY_KEYS)[number]>,
    ];

/**
 * Fails to compile if the key list ever falls behind the type: the annotation
 * resolves to an error tuple, and `true` is not assignable to it.
 */
export const USER_LICENSE_ENTRY_KEYS_ARE_EXHAUSTIVE: UserLicenseEntryKeysAreExhaustive =
  true;

/**
 * Narrows an unknown value to a {@link UserLicenseEntry}.
 *
 * Lives beside the type rather than in the consumer, so the guard travels with
 * the thing it guards and a second consumer cannot hand-roll a third variant.
 */
export function isUserLicenseEntry(value: unknown): value is UserLicenseEntry {
  // An array is `typeof 'object'`, so an array carrying the four keys as named
  // properties would otherwise narrow to an entry and reach the table as a row
  // with no cells.
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const candidate: Record<string, unknown> = { ...value };
  return USER_LICENSE_ENTRY_KEYS.every(key => {
    const field = candidate[key];
    // Non-empty, because an empty string is not an id, a licence name or a
    // date: it renders as a blank cell, which reads as "this licence has no
    // name" rather than as the malformed payload it is. The backend's Zod
    // schema rejects the same shape with `.min(1)`; this is the browser-side
    // half of that, for a payload that reached the page some other way.
    return typeof field === 'string' && field.length > 0;
  });
}

/**
 * Orders two license entries deterministically: by `licenseTypeName`, then
 * `startDate`, then `id`. Uses plain ordinal string comparison (not
 * `localeCompare`) so the order cannot vary with locale/ICU data across
 * environments.
 */
export function compareLicenseEntries(
  first: Pick<UserLicenseEntry, 'id' | 'licenseTypeName' | 'startDate'>,
  second: Pick<UserLicenseEntry, 'id' | 'licenseTypeName' | 'startDate'>,
): number {
  if (first.licenseTypeName !== second.licenseTypeName) {
    return first.licenseTypeName < second.licenseTypeName ? -1 : 1;
  }
  if (first.startDate !== second.startDate) {
    return first.startDate < second.startDate ? -1 : 1;
  }
  if (first.id !== second.id) {
    return first.id < second.id ? -1 : 1;
  }
  return 0;
}

/**
 * Displays Tessa's ISO-shaped start date without applying the viewer's timezone.
 * Values outside the supported date shape are returned verbatim.
 */
export function formatLicenseStartDate(startDate: string): string {
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(startDate)
    ? startDate.slice(0, 10)
    : startDate;
}
